<template>
  <div class="week-calendar">
    <div class="d-flex justify-content-between align-items-center mb-2">
      <b-button size="sm" variant="outline-primary" @click="prevWeek">Prev</b-button>
      <div class="h6 m-0">{{ rangeLabel }}</div>
      <b-button size="sm" variant="outline-primary" @click="nextWeek">Next</b-button>
    </div>
    <div class="grid">
      <div class="time-col">
        <div class="time-slot" v-for="t in times" :key="t">{{ t }}</div>
      </div>
      <div class="day-col" v-for="(day, idx) in days" :key="idx">
        <div class="day-header">{{ day.label }}</div>
        <div class="day-body">
          <div class="time-slot" v-for="t in times" :key="day.date.toISOString()+t"></div>
          <div
            v-for="(ev, i) in eventsByDay[day.key]"
            :key="i"
            :class="['event', statusClass(ev.post)]"
            :style="eventStyle(ev)"
            :title="eventTitle(ev)"
            @click="$emit('post-click', ev.post)"
          >
            <div class="event-title">{{ eventTitle(ev) }}</div>
            <div class="event-time">{{ formatTime(ev.start) }} – {{ formatTime(ev.end) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PostsWeekCalendar',
  props: {
    posts: { type: Array, default: () => [] },
    value: { type: Date, default: () => new Date() },
    timezone: { type: String, default: 'local' },
  },
  data() {
    const start = this.startOfWeek(new Date(this.value))
    return {
      weekStart: start,
      hourStart: 0,
      hourEnd: 24,
    }
  },
  computed: {
    rangeLabel() {
      const end = new Date(this.weekStart)
      end.setDate(end.getDate() + 6)
      const fmt = d => d.toLocaleDateString(undefined, { month: 'short', day: '2-digit' })
      return `${fmt(this.weekStart)} – ${fmt(end)}`
    },
    days() {
      const ds = []
      for (let i = 0; i < 7; i++) {
        const d = new Date(this.weekStart)
        d.setDate(d.getDate() + i)
        ds.push({
          date: d,
          key: this.dayKey(d),
          label: d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' }),
        })
      }
      return ds
    },
    times() {
      const arr = []
      for (let h = this.hourStart; h < this.hourEnd; h++) {
        arr.push(`${String(h).padStart(2,'0')}:00`)
      }
      return arr
    },
    eventsByDay() {
      const map = {}
      this.days.forEach(d => { map[d.key] = [] })
      ;(this.posts || []).forEach(p => {
        const start = p.scheduleDate ? new Date(p.scheduleDate) : (p.created ? new Date(p.created) : null)
        if (!start) return
        const end = new Date(start)
        end.setMinutes(end.getMinutes() + 60)
        const key = this.dayKey(start)
        if (!map[key]) return
        map[key].push({ post: p, start, end })
      })
      return map
    },
  },
  methods: {
    startOfWeek(d) {
      const x = new Date(d)
      const wd = x.getDay()
      x.setDate(x.getDate() - wd)
      x.setHours(0,0,0,0)
      return x
    },
    dayKey(d) {
      return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
    },
    prevWeek() {
      const d = new Date(this.weekStart)
      d.setDate(d.getDate() - 7)
      this.weekStart = d
    },
    nextWeek() {
      const d = new Date(this.weekStart)
      d.setDate(d.getDate() + 7)
      this.weekStart = d
    },
    formatTime(d) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    eventTitle(ev) {
      const p = ev.post
      if (p && typeof p.post === 'string' && p.post.length) return p.post.slice(0, 32)
      return 'Post'
    },
    eventStyle(ev) {
      const startMinutes = ev.start.getHours()*60 + ev.start.getMinutes()
      const top = (startMinutes/60 - this.hourStart) * 48 // 48px per hour
      const height = 48 // 1h
      return {
        top: `${top}px`,
        height: `${height}px`,
      }
    },
    statusClass(post) {
      const s = (post && post.status) ? String(post.status).toLowerCase() : ''
      if (s === 'success' || s === 'posted') return 'status-success'
      if (s === 'pending' || s === 'scheduled') return 'status-pending'
      if (s === 'error' || s === 'failed') return 'status-error'
      return ''
    }
  }
}
</script>

<style scoped>
.grid { display: grid; grid-template-columns: 80px repeat(7, 1fr); grid-gap: 6px; }
.time-col { min-width: 80px; }
.time-slot { height: 48px; font-size: 11px; color: #666; }
.day-col { position: relative; }
.day-header { text-align: center; font-weight: 600; margin-bottom: 6px; }
.day-body { position: relative; border: 1px solid #e9ecef; border-radius: 6px; min-height: calc(48px * 24); }
.day-body .time-slot { border-top: 1px dashed #f1f3f5; }
.event { position: absolute; left: 6px; right: 6px; background: #6c8ef5; color: #fff; border-radius: 6px; padding: 4px 6px; font-size: 11px; overflow: hidden; cursor: pointer; }
.event.status-success { background: #2e7d32; }
.event.status-pending { background: #ef6c00; }
.event.status-error { background: #c62828; }
.event-title { font-weight: 600; white-space: nowrap; text-overflow: ellipsis; overflow: hidden; }
.event-time { opacity: 0.85; }
</style>

